import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { FileDown, Loader2, LayoutList } from "lucide-react";
import { toast } from "sonner";

const Broadsheet = () => {
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [term, setTerm] = useState("first");
  const [programId, setProgramId] = useState("");
  const [classId, setClassId] = useState("");
  const [armId, setArmId] = useState("");
  const [bs, setBs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, se] = await Promise.all([api.get("/programs", { params: { active_only: true } }), api.get("/academic-sessions")]);
        const enriched = await Promise.all(pRes.data.map(async (p) => {
          const cls = (await api.get("/classes", { params: { program_id: p.id } })).data;
          return { ...p, classes: cls };
        }));
        setPrograms(enriched); setSessions(se.data);
        const cur = se.data.find((s) => s.is_current) || se.data[0];
        if (cur) setSessionId(cur.id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const prog = programs.find((p) => p.id === programId);
  const cls = (prog?.classes || []).find((c) => c.id === classId);
  const arms = cls?.arms || [];

  const generate = async () => {
    if (!sessionId || !classId) return toast.error("Pick a session and class");
    setLoading(true); setBs(null);
    try {
      const { data } = await api.get("/assessments/broadsheet", { params: { class_id: classId, arm_id: armId || undefined, session_id: sessionId, term } });
      setBs(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const downloadPdf = () => {
    api.get("/assessments/broadsheet/pdf", { params: { class_id: classId, arm_id: armId || undefined, session_id: sessionId, term }, responseType: "blob" })
      .then((res) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        link.download = `Broadsheet_${cls?.name || "class"}_${term}.pdf`;
        document.body.appendChild(link); link.click(); link.remove();
      }).catch((e) => toast.error(formatApiError(e)));
  };
  const downloadXlsx = () => {
    api.get("/assessments/broadsheet/xlsx", { params: { class_id: classId, arm_id: armId || undefined, session_id: sessionId, term }, responseType: "blob" })
      .then((res) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([res.data]));
        link.download = `Broadsheet_${cls?.name || "class"}_${term}.xlsx`;
        document.body.appendChild(link); link.click(); link.remove();
      }).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div data-testid="broadsheet-page">
      <PageHeader
        overline="Assessment"
        title="Broadsheet"
        description="Class-wide result matrix showing every student's scores across every subject, with subject averages, highest/lowest, and class positions."
      />

      <Card className="border-[#E2E8F0] p-4 mb-4">
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <div><label className="text-xs text-[#1B2C5A] block mb-1">Session</label>
            <Select value={sessionId} onValueChange={setSessionId}><SelectTrigger className="h-9 bg-white" data-testid="bs-session"><SelectValue /></SelectTrigger>
              <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-[#1B2C5A] block mb-1">Term</label>
            <Select value={term} onValueChange={setTerm}><SelectTrigger className="h-9 bg-white" data-testid="bs-term"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="first">First</SelectItem><SelectItem value="second">Second</SelectItem><SelectItem value="third">Third</SelectItem></SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-[#1B2C5A] block mb-1">Program</label>
            <Select value={programId} onValueChange={(v) => { setProgramId(v); setClassId(""); setArmId(""); }}><SelectTrigger className="h-9 bg-white" data-testid="bs-program"><SelectValue placeholder="Pick program" /></SelectTrigger>
              <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-[#1B2C5A] block mb-1">Class</label>
            <Select value={classId} onValueChange={(v) => { setClassId(v); setArmId(""); }} disabled={!prog}><SelectTrigger className="h-9 bg-white" data-testid="bs-class"><SelectValue placeholder="Pick class" /></SelectTrigger>
              <SelectContent>{(prog?.classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-[#1B2C5A] block mb-1">Arm (optional)</label>
            <Select value={armId} onValueChange={setArmId} disabled={!arms.length}><SelectTrigger className="h-9 bg-white" data-testid="bs-arm"><SelectValue placeholder="All arms" /></SelectTrigger>
              <SelectContent>{arms.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={generate} disabled={!classId || loading} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white h-9" data-testid="bs-generate">
            {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Building…</> : <><LayoutList className="w-4 h-4 mr-1" /> Generate</>}
          </Button>
        </div>
      </Card>

      {bs && (
        <Card className="border-[#E2E8F0] overflow-hidden" data-testid="bs-result">
          <div className="p-4 flex flex-wrap items-center gap-3 border-b border-[#E2E8F0]">
            <div>
              <div className="font-display text-lg text-[#1B2C5A]">{bs.class.name}{bs.class.arm_name ? ` · ${bs.class.arm_name}` : ""}</div>
              <div className="text-xs text-[#475569]">{bs.term_label} · {bs.session.name} · {bs.rows.length} students · {bs.subjects.length} subjects</div>
            </div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" onClick={downloadPdf} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="bs-pdf"><FileDown className="w-4 h-4 mr-1.5" /> PDF</Button>
              <Button size="sm" variant="outline" onClick={downloadXlsx} className="border-[#E2E8F0]" data-testid="bs-xlsx"><FileDown className="w-4 h-4 mr-1.5" /> Excel</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FDFBF7]">
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Admission</TableHead>
                  <TableHead>Student</TableHead>
                  {bs.subjects.map((s) => <TableHead key={s.id} className="text-center whitespace-nowrap">{s.name}</TableHead>)}
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">Avg %</TableHead>
                  <TableHead className="text-center">Pos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bs.rows.map((r, i) => (
                  <TableRow key={r.student_id} data-testid={`bs-row-${r.student_id}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{r.admission_no}</TableCell>
                    <TableCell className="font-medium text-[#1B2C5A]">{r.full_name}</TableCell>
                    {bs.subjects.map((s) => {
                      const ps = r.per_subject[s.id];
                      return <TableCell key={s.id} className="text-center">{ps ? ps.total : "—"}</TableCell>;
                    })}
                    <TableCell className="text-center font-semibold">{r.grand_total}</TableCell>
                    <TableCell className="text-center">{r.avg_pct}%</TableCell>
                    <TableCell className="text-center font-semibold text-[#1B2C5A]">{r.position_ordinal || "—"}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-[#9DCD3F]/10 font-medium">
                  <TableCell colSpan={3} className="text-right text-[#475569]">Subject Average</TableCell>
                  {bs.subjects.map((s) => <TableCell key={s.id} className="text-center">{s.average ?? "—"}</TableCell>)}
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
                <TableRow className="bg-emerald-50/70">
                  <TableCell colSpan={3} className="text-right text-emerald-800">Highest</TableCell>
                  {bs.subjects.map((s) => <TableCell key={s.id} className="text-center text-emerald-800">{s.highest ?? "—"}</TableCell>)}
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
                <TableRow className="bg-red-50/60">
                  <TableCell colSpan={3} className="text-right text-red-800">Lowest</TableCell>
                  {bs.subjects.map((s) => <TableCell key={s.id} className="text-center text-red-800">{s.lowest ?? "—"}</TableCell>)}
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      {!bs && !loading && <EmptyState message="Pick a session, term, program and class then click Generate." testId="bs-empty" />}
    </div>
  );
};

export default Broadsheet;
